import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { IComment } from "../../../api/comments";
import { useAppSelector } from "../../../app/store";
import Avatar from "../../../components/ui/avatar/Avatar";
import CansoEditor from "../../../components/ui/editor";
import { cn } from "../../../lib/utils";
import { formatCommentTime } from "../utils/formatCommentTime";
import { getUsersByEmailIds } from "../../users/logic/slice";

const isLikelyMarkdownTable = (markdown: string) => {
  return /\|.*\|\s*\n\|[\s:\-|]+\|\s*\n\|.*\|/m.test(markdown);
};

export interface CommentProps {
  comment: IComment;
  now?: Date;
  className?: string;
}

export const Comment: React.FC<CommentProps> = ({
  comment,
  now,
  className,
}) => {
  const textRef = useRef<HTMLDivElement | null>(null);

  const usersByEmailId = useAppSelector(state => getUsersByEmailIds(state, [comment.commentor]));
  const commentorName =
    usersByEmailId[comment.commentor]?.user_name || comment.commentor;

  const [isExpanded, setIsExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);

  const hasTable = useMemo(
    () => isLikelyMarkdownTable(comment.comment),
    [comment.comment],
  );

  const timeText = useMemo(
    () => formatCommentTime(comment.commented_at, () => now ?? new Date()),
    [comment.commented_at, now],
  );

  useLayoutEffect(() => {
    if (hasTable) {
      setIsExpanded(true);
      setIsTruncated(false);
      return;
    }

    if (isExpanded) {
      setIsTruncated(false);
      return;
    }

    const el = textRef.current;
    if (!el) return;
    let raf1 = 0;
    let raf2 = 0;

    const measure = () => {
      setIsTruncated(el.scrollHeight > el.clientHeight + 1);
    };

    raf1 = window.requestAnimationFrame(() => {
      measure();
      raf2 = window.requestAnimationFrame(measure);
    });

    return () => {
      window.cancelAnimationFrame(raf1);
      window.cancelAnimationFrame(raf2);
    };
  }, [comment.comment, isExpanded, hasTable]);

  const renderHeader = () => {
    return (
      <div className="flex items-center justify-between gap-3">
        <span className="font-sans font-medium text-sm leading-5 text-[#0A0A0A]">
          {commentorName}
        </span>
        <span className="font-sans font-normal text-sm leading-5 text-[#737373]">
          {timeText}
        </span>
      </div>
    );
  };

  const renderBody = () => {
    return (
      <div className="flex flex-col gap-1">
        <div
          ref={textRef}
          className={cn(
            "min-w-0",
            isExpanded ? "" : "max-h-[60px] overflow-hidden",
          )}
        >
          <CansoEditor
            id={`comment-readonly-${comment.comment_id}`}
            markdown={comment.comment}
            readOnly
            showToolbar={false}
            autoHideToolbar={false}
            allowFullScreen={false}
            className={cn(
              "bg-transparent",
              "!leading-5",
              "[&_.mdxeditor-root-contenteditable]:p-0",
              "[&_.mdxeditor-root-contenteditable]:overflow-hidden",
              "[&_p]:mb-0",
              "[&_p]:font-sans",
              "[&_p]:font-normal",
              "[&_p]:text-sm",
              "[&_p]:leading-5",
              "[&_p]:text-[#737373]",
              "[&_p]:break-words",
              "[&_strong]:text-[#737373]",
              "[&_em]:text-[#737373]",
            )}
            contentEditableClassName="h-auto overflow-hidden px-0 py-0"
          />
        </div>

        {!isExpanded && isTruncated && (
          <button
            type="button"
            onClick={() => setIsExpanded(true)}
            className="
              w-fit
              font-sans font-semibold text-sm leading-5
              underline
              text-[#737373]
            "
          >
            See more
          </button>
        )}
      </div>
    );
  };

  return (
    <div className={cn("flex gap-3", className)}>
      <Avatar name={commentorName} />
      <div className="flex flex-col gap-1 min-w-0 flex-1">
        {renderHeader()}
        {renderBody()}
      </div>
    </div>
  );
};

export default Comment;
